import { LoadingOutlined } from "@ant-design/icons";
import {
  ArchiveIcon,
  BadgeAlertIcon,
  BellIcon,
  BookmarkIcon,
  ChevronDownIcon,
  ClipboardCheckIcon,
  ClockIcon,
  CloudCogIcon,
  DownloadIcon,
  FolderOpenIcon,
  HistoryIcon,
  IdCardIcon,
  LayoutGridIcon,
  LogoutIcon,
  MapPinIcon,
  MenuIcon,
  MoonIcon,
  PanelLeftCloseIcon,
  PanelLeftOpenIcon,
  SettingsIcon,
  SunIcon,
  UserIcon,
  UserRoundCheckIcon,
  UserRoundPlusIcon,
  UsersIcon,
  WalletIcon,
  WrenchIcon,
} from "lucide-animated";
import {
  Avatar,
  Button,
  Drawer,
  Layout,
  Popover,
  Skeleton,
  Typography,
} from "antd";
import React, { memo, useCallback, useRef, useState } from "react";
import { UserAvatar } from "@/shared/components/UserAvatar.js";
import { Link, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { container } from "../shared/di/container";
import { useAcl } from "../core/acl/useAcl";
import { useAppState } from "../core/app_state/AppStateContext";
import { useTheme } from "../core/theme/ThemeContext";
import { useAuthSession } from "../features/authentication/presentation/hooks/useAuth";
import { useAuthActions } from "../features/authentication/presentation/hooks/useAuthActions";
import { appName } from "../shared/lib/getAppName";
import { AppLogo } from "../shared/components/AppLogo";

const { Sider } = Layout;
const { Text } = Typography;

export const SIDEBAR_WIDTH = 220;
export const SIDEBAR_COLLAPSED_WIDTH = 56;
export const TOPBAR_HEIGHT = 52;

const menuItems = [
  {
    type: "group" as const,
    label: "TỔNG QUAN",
    children: [
      { key: "/", icon: <LayoutGridIcon size={16} />, label: "Dashboard" },
      { key: "/projects", icon: <FolderOpenIcon size={16} />, label: "Projects", aclResource: "projects", aclAction: "read" },
      { key: "/notifications", icon: <BellIcon size={16} />, label: "Thông báo" },
    ],
  },
  {
    type: "group" as const,
    label: "VẬN HÀNH",
    children: [
      { key: "/locations", icon: <MapPinIcon size={16} />, label: "Địa điểm", aclResource: "locations", aclAction: "manage" },
      {
        key: "/sessions",
        icon: <ClockIcon size={16} />,
        label: "Lịch làm việc",
        aclResource: "sessions",
        aclAction: "manage",
      },
      { key: "/stock", icon: <ArchiveIcon size={16} />, label: "Kho hàng", aclResource: "stocks/assets", aclAction: "manage" },
      { key: "/issues", icon: <BadgeAlertIcon size={16} />, label: "Hỗ Trợ", aclResource: "issues", aclAction: "manage" },
      { type: "divider" },
      { key: "/formx", icon: <ClipboardCheckIcon size={16} />, label: "Mẫu biểu", aclResource: "formx", aclAction: "manage" },
      { key: "/members", icon: <UsersIcon size={16} />, label: "Members", aclResource: "memberships", aclAction: "manage" },
      { key: "/contacts", icon: <UserRoundCheckIcon size={16} />, label: "Khách hàng", aclResource: "contacts", aclAction: "manage" },
      { key: "/metadata", icon: <BookmarkIcon size={16} />, label: "Metadata", aclResource: "metadatas", aclAction: "manage" },
      { key: "/audit-log", icon: <HistoryIcon size={16} />, label: "Nhật ký", aclResource: "audit_log", aclAction: "manage" },
    ],
  },
  {
    type: "group" as const,
    label: "TOOLS",
    children: [{ key: "/tools", icon: <WrenchIcon size={16} />, label: "Tools" }],
  },
  {
    type: "group" as const,
    label: "QUẢN TRỊ",
    children: [
      {
        key: "/sourcing",
        icon: <UserRoundPlusIcon size={16} />,
        label: "Sourcing Nhân Sự",
        aclResource: "profiles",
        aclAction: "manage",
      },
      { key: "/users", icon: <IdCardIcon size={16} />, label: "Users", aclResource: "profiles", aclAction: "manage" },
      { key: "/orgs", icon: <WalletIcon size={16} />, label: "Orgs", aclResource: "orgs", aclAction: "manage" },
      {
        key: "/admin/configurations",
        icon: <SettingsIcon size={16} />,
        label: "Cấu hình",
        superAdminOnly: true,
      },
      {
        key: "/admin/exports",
        icon: <DownloadIcon size={16} />,
        label: "Cấp quyền Export",
        superAdminOnly: true,
      },
    ],
  },
  {
    type: "group" as const,
    label: "SUPER ADMIN",
    children: [
      {
        key: "/admin/super-admin",
        icon: <CloudCogIcon size={16} />,
        label: "Super Admin",
        superAdminOnly: true,
      },
      {
        key: "/admin/super-admin/system-config",
        icon: <SettingsIcon size={16} />,
        label: "Cấu hình hệ thống",
        superAdminOnly: true,
      },
    ],
  },
];

const MOBILE_PRIMARY = [
  { key: "/", icon: <LayoutGridIcon size={19} />, label: "Dashboard" },
  { key: "/projects", icon: <FolderOpenIcon size={19} />, label: "Dự án" },
  { key: "/sessions", icon: <ClockIcon size={19} />, label: "Lịch" },
  { key: "/notifications", icon: <BellIcon size={19} />, label: "Thông báo" },
] as const;

export const ROOT_PATHS: ReadonlySet<string> = new Set([
  "/",
  ...menuItems.flatMap((group) =>
    (group.children ?? [])
      .filter((item): item is typeof item & { key: string } => "key" in item)
      .map((item) => item.key),
  ),
  "/kpis",
  "/accountant/payouts",
  "/notifications",
]);

const MENU_KEYS = menuItems
  .flatMap((group) =>
    (group.children ?? [])
      .filter((item): item is typeof item & { key: string } => "key" in item)
      .map((item) => item.key),
  )
  .filter((k) => k !== "/")
  .sort((a, b) => b.length - a.length);

export function resolveMenuKey(pathname: string): string {
  if (pathname === "/") return "/";
  const match = MENU_KEYS.find(
    (k) => pathname === k || pathname.startsWith(`${k}/`),
  );
  return match ?? pathname;
}

const siderStyle: React.CSSProperties = {
  background: "color-mix(in srgb, var(--accent) 6%, var(--bg2))",
  borderRight: "1px solid var(--border)",
  position: "fixed",
  height: "100vh",
  left: 0,
  top: 0,
  zIndex: 100,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  transition: "width 0.2s",
};

const logoStyle: React.CSSProperties = {
  height: TOPBAR_HEIGHT,
  display: "flex",
  gap: 10,
  alignItems: "center",
  flexShrink: 0,
  position: "relative",
};

const logoMarkStyle: React.CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: 6,
  flexShrink: 0,
  objectFit: "contain",
};

const navScrollStyle: React.CSSProperties = {
  height: "100%",
  overflowY: "auto",
  padding: "8px 0",
  scrollbarWidth: "none",
};

const logoutWrapStyle: React.CSSProperties = {
  padding: "12px 8px",
  borderTop: "1px solid var(--border)",
  flexShrink: 0,
};

export const avatarStyle: React.CSSProperties = {
  background: "var(--accent)",
  color: "var(--accent-ink)",
  fontSize: 13,
  fontWeight: 600,
  flexShrink: 0,
};

const railAvatarStyle: React.CSSProperties = {
  ...avatarStyle,
  cursor: "pointer",
  margin: "0 auto",
  display: "flex",
};

const orgSquareSmallStyle: React.CSSProperties = {
  width: 24,
  height: 24,
  fontSize: 11,
};

const railPopoverContentStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
  minWidth: 140,
};

const railBtnStyle: React.CSSProperties = { justifyContent: "flex-start" };

export interface AppSidebarProps {
  selectedKey: string;
  isMobile: boolean;
  // desktop: manually collapsed by user (sticky)
  collapsed: boolean;
  onUserCollapse: (v: boolean) => void;
  // mobile: drawer open state
  drawerOpen: boolean;
  onDrawerClose: () => void;
}

export const AppSidebar = memo(function AppSidebar({
  selectedKey,
  isMobile,
  collapsed,
  onUserCollapse,
  drawerOpen,
  onDrawerClose,
}: AppSidebarProps) {
  const { activeOrg, orgs, orgsLoading, setActiveOrg } = useAppState();
  const { isSuperAdmin, can } = useAcl();
  const { logoutMutation } = useAuthActions();
  const { isDark, toggleTheme, sidebarTransparent } = useTheme();
  const { data: session } = useAuthSession();
  const navigate = useNavigate();

  const navRef = useRef<HTMLDivElement>(null);
  const fadeRef = useRef<HTMLDivElement>(null);

  const queryClient = useQueryClient();
  const lastOrgRefresh = useRef(0);

  // Desktop rail: when collapsed, hovering temporarily expands to show labels
  // (Linear-style) without changing the user's sticky collapsed pref or the
  // content margin — the wide sider just overlays the content.
  const [railHovered, setRailHovered] = useState(false);
  const [orgPopoverOpen, setOrgPopoverOpen] = useState(false);
  // Effective expanded view: mobile drawer always full; desktop full when not
  // collapsed, or transiently while hovering the rail.
  const expanded = isMobile || !collapsed || railHovered;

  const userInitial = session?.email?.[0]?.toUpperCase() ?? "U";
  const userName = session?.email?.split("@")[0] ?? "";
  const uid = session?.uid;
  const orgInitial = activeOrg?.name?.[0]?.toUpperCase() ?? "?";

  // Opening the org switcher re-mints the JWT so newly-granted org memberships
  // (baked into rbac.orgs at token issue time) show up without a full re-login.
  // Throttled to avoid spamming the token endpoint on repeated open/close.
  const handleOrgDropdownOpen = useCallback(
    async (open: boolean) => {
      if (!open) return;
      if (Date.now() - lastOrgRefresh.current < 30_000) return;
      lastOrgRefresh.current = Date.now();
      try {
        await container.auth.refreshSessionUseCase();
        await queryClient.invalidateQueries({ queryKey: ["orgs"] });
      } catch {
        // keep the current (stale) list if refresh fails
      }
    },
    [queryClient],
  );

  const handleNavScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    if (!fadeRef.current) return;
    const el = e.currentTarget;
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    fadeRef.current.style.opacity = String(Math.min(remaining / 60, 1));
  }, []);

  const handleOrgChange = useCallback(
    (id: string) => {
      const org = orgs.find((o) => o.id === id);
      if (org) {
        setActiveOrg(org);
        setOrgPopoverOpen(false);
        navigate("/");
      }
    },
    [orgs, setActiveOrg, navigate],
  );

  const handleOrgPopoverOpen = useCallback(
    (open: boolean) => {
      setOrgPopoverOpen(open);
      handleOrgDropdownOpen(open);
    },
    [handleOrgDropdownOpen],
  );

  const orgPopoverContent = (
    <div className="org-pop-list">
      {orgs.length === 0 ? (
        <Text className="t-tertiary-12" style={{ padding: 8 }}>
          Chưa có tổ chức
        </Text>
      ) : (
        orgs.map((o) => {
          const active = o.id === activeOrg?.id;
          return (
            <div
              key={o.id}
              className={`org-pop-item${active ? " org-pop-item--active" : ""}`}
              onClick={() => o.id && handleOrgChange(o.id)}
            >
              <div className="org-square" style={orgSquareSmallStyle}>
                {o.name?.[0]?.toUpperCase() ?? "?"}
              </div>
              <Text
                style={{
                  color: active ? "var(--accent)" : "var(--text)",
                  fontSize: 13,
                  fontWeight: active ? 600 : 400,
                }}
                ellipsis
              >
                {o.name}
              </Text>
            </div>
          );
        })
      )}
    </div>
  );

  const sidebarInner = (
    <>
      <div
        style={{
          ...logoStyle,
          justifyContent: expanded ? "flex-start" : "center",
          paddingLeft: expanded ? 16 : 0,
        }}
      >
        <Link to="/">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <AppLogo size={28} style={logoMarkStyle} />
            {expanded && (
              <Text
                strong
                style={{
                  color: "var(--text)",
                  fontSize: 14,
                  letterSpacing: "-0.02em",
                }}
              >
                {appName}
              </Text>
            )}
          </div>
        </Link>
        <div className="sb-logo-divider" />
      </div>

      <div className="sb-orghead">
        {orgsLoading ? (
          <Skeleton.Input
            active
            size="small"
            style={{ width: "100%", height: 36 }}
          />
        ) : (
          <Popover
            trigger="click"
            placement="rightTop"
            open={orgPopoverOpen}
            onOpenChange={handleOrgPopoverOpen}
            content={orgPopoverContent}
          >
            <button
              type="button"
              className={`org-head-btn${expanded ? "" : " org-head-btn--rail"}`}
              title={activeOrg?.name ?? "Chọn tổ chức"}
            >
              <div className="org-square org-square--muted">{orgInitial}</div>
              {expanded && (
                <>
                  <span className="org-name" style={{ flex: 1, minWidth: 0 }}>
                    {activeOrg?.name ?? "Chọn tổ chức..."}
                  </span>
                  <ChevronDownIcon
                    size={14}
                    style={{
                      color: "var(--text3)",
                      flexShrink: 0,
                    }}
                  />
                </>
              )}
            </button>
          </Popover>
        )}
      </div>

      <div style={{ flex: 1, minHeight: 0, position: "relative" }}>
        <div
          ref={navRef}
          className="sidebar-nav-scroll"
          style={navScrollStyle}
          onScroll={handleNavScroll}
        >
          <nav className="sb-nav">
            {menuItems.map((group) => {
              const visibleChildren = group.children.filter((item) => {
                if ("superAdminOnly" in item && item.superAdminOnly && !isSuperAdmin) {
                  return false;
                }
                if ("aclResource" in item && item.aclResource && "aclAction" in item && item.aclAction) {
                  if (!can(item.aclResource, item.aclAction)) return false;
                }
                return true;
              });
              if (visibleChildren.length === 0) return null;
              return (
                <div key={group.label} className="sb-group">
                  {expanded && (
                    <div className="sb-group-title">{group.label}</div>
                  )}
                  {visibleChildren.map((item, i) => {
                    if (!item.key) {
                      return (
                        <div key={`divider-${i}`} className="sb-item-divider" />
                      );
                    }
                    const on = selectedKey === item.key;
                    return (
                      <Link
                        key={item.key}
                        to={item.key}
                        className={`sb-item${on ? " is-on" : ""}`}
                        onClick={() => {
                          if (isMobile) onDrawerClose();
                        }}
                        title={expanded ? undefined : item.label}
                      >
                        <span className="sb-item-icon">{item.icon}</span>
                        {expanded && (
                          <span className="sb-item-label">{item.label}</span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              );
            })}
          </nav>
        </div>
        <div ref={fadeRef} className="sidebar-scroll-fade" />
      </div>

      <div style={logoutWrapStyle}>
        {!expanded ? (
          <Popover
            placement="rightBottom"
            trigger="click"
            content={
              <div style={railPopoverContentStyle}>
                {uid && (
                  <Button
                    type="text"
                    icon={<UserIcon size={14} />}
                    onClick={() => navigate(`/users/${uid}`)}
                    style={railBtnStyle}
                  >
                    Hồ sơ
                  </Button>
                )}
                <Button
                  type="text"
                  icon={isDark ? <SunIcon size={14} /> : <MoonIcon size={14} />}
                  onClick={toggleTheme}
                  style={railBtnStyle}
                >
                  {isDark ? "Chế độ sáng" : "Chế độ tối"}
                </Button>
                <Button
                  type="text"
                  danger
                  icon={<LogoutIcon size={14} />}
                  loading={logoutMutation.isPending}
                  onClick={() => logoutMutation.mutate()}
                  style={railBtnStyle}
                >
                  Đăng xuất
                </Button>
              </div>
            }
          >
            <UserAvatar
              size={28}
              style={railAvatarStyle}
              user={{
                id: uid,
                email: session?.email,
                avatarUrl: session?.photoURL,
              }}
            >
              {userInitial}
            </UserAvatar>
          </Popover>
        ) : (
          <div className="sb-footer">
            <button
              type="button"
              className="sb-profile"
              onClick={() => uid && navigate(`/users/${uid}`)}
            >
              <UserAvatar
                size={32}
                style={avatarStyle}
                user={{
                  id: uid,
                  email: session?.email,
                  avatarUrl: session?.photoURL,
                }}
              >
                {userInitial}
              </UserAvatar>
              <span className="sb-meta">
                <span className="sb-name">{userName}</span>
                <span className="sb-email">{session?.email}</span>
              </span>
            </button>
            <div className="sb-footer-actions">
              <button
                type="button"
                className="sb-icon-btn"
                title={isDark ? "Chế độ sáng" : "Chế độ tối"}
                onClick={toggleTheme}
              >
                {isDark ? <SunIcon size={14} /> : <MoonIcon size={14} />}
              </button>
              <button
                type="button"
                className="sb-icon-btn"
                title="Cài đặt"
                onClick={() => uid && navigate(`/users/${uid}`)}
              >
                <SettingsIcon size={14} />
              </button>
              <button
                type="button"
                className="sb-icon-btn is-danger"
                title="Đăng xuất"
                disabled={logoutMutation.isPending}
                onClick={() => logoutMutation.mutate()}
              >
                {logoutMutation.isPending ? (
                  <LoadingOutlined spin />
                ) : (
                  <LogoutIcon size={14} />
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );

  if (isMobile) {
    return (
      <Drawer
        open={drawerOpen}
        placement="left"
        size={SIDEBAR_WIDTH}
        onClose={onDrawerClose}
        closable={false}
        styles={{
          body: {
            padding: 0,
            display: "flex",
            flexDirection: "column",
            background: "color-mix(in srgb, var(--accent) 6%, var(--bg2))",
            height: "100%",
          },
        }}
        style={{ zIndex: 200 }}
      >
        {sidebarInner}
      </Drawer>
    );
  }

  const railWidth = expanded ? SIDEBAR_WIDTH : SIDEBAR_COLLAPSED_WIDTH;

  return (
    <>
      <Sider
        width={SIDEBAR_WIDTH}
        collapsedWidth={SIDEBAR_COLLAPSED_WIDTH}
        collapsed={!expanded}
        className={`app-sider${collapsed ? " app-sider--rail" : ""}`}
        style={
          sidebarTransparent
            ? {
                ...siderStyle,
                background: "color-mix(in srgb, var(--bg2) 55%, transparent)",
                backdropFilter: "blur(16px)",
                WebkitBackdropFilter: "blur(16px)",
                borderRight:
                  "1px solid color-mix(in srgb, var(--border) 60%, transparent)",
              }
            : siderStyle
        }
        trigger={null}
        onMouseEnter={() => collapsed && setRailHovered(true)}
        onMouseLeave={() => {
          if (!orgPopoverOpen) setRailHovered(false);
        }}
      >
        {sidebarInner}
      </Sider>
      <button
        type="button"
        className="sb-toggle"
        onClick={() => onUserCollapse(!collapsed)}
        aria-label={collapsed ? "Ghim mở thanh bên" : "Thu gọn thanh bên"}
        title={collapsed ? "Ghim mở thanh bên" : "Thu gọn thanh bên"}
        style={{
          position: "fixed",
          left: railWidth - 12,
          top: TOPBAR_HEIGHT / 2,
          right: "auto",
          transform: "translateY(-50%)",
          zIndex: 101,
          transition: "left 0.2s",
        }}
      >
        {collapsed ? <PanelLeftOpenIcon size={14} /> : <PanelLeftCloseIcon size={14} />}
      </button>
    </>
  );
});

export const MobileBottomNav = memo(function MobileBottomNav({
  selectedKey,
  onMore,
}: {
  selectedKey: string;
  onMore: () => void;
}) {
  const activeKey =
    MOBILE_PRIMARY.find((it) =>
      it.key === "/" ? selectedKey === "/" : selectedKey.startsWith(it.key),
    )?.key ?? "";

  return (
    <nav className="mobile-bottom-nav">
      {MOBILE_PRIMARY.map((it) => (
        <Link
          key={it.key}
          to={it.key}
          className={`mbn-item${activeKey === it.key ? " mbn-item--active" : ""}`}
        >
          {it.icon}
          <span>{it.label}</span>
        </Link>
      ))}
      <button type="button" className="mbn-item" onClick={onMore}>
        <MenuIcon size={19} />
        <span>Thêm</span>
      </button>
    </nav>
  );
});
